/**
 * Module Demo Page - Development only
 * Shows all 14 module visualizations with sample data
 */
import {
  DnsModuleView,
  EmailModuleView,
  TlsModuleView,
  HeadersModuleView,
  WhoisModuleView,
  TechModuleView,
  CloudModuleView,
  AsnModuleView,
  ReposModuleView,
  CrawlHintsModuleView,
  JsAnalysisModuleView,
  FaviconModuleView,
  SubdomainsModuleView,
  PortscanModuleView,
  type DnsModuleData,
  type EmailModuleData,
  type TlsModuleData,
  type HeadersModuleData,
  type WhoisModuleData,
  type TechModuleData,
  type CloudModuleData,
  type AsnModuleData,
  type ReposModuleData,
  type CrawlHintsModuleData,
  type JsAnalysisModuleData,
  type FaviconModuleData,
  type SubdomainsModuleData,
  type PortscanModuleData,
} from "@asm/ui";

// Sample data for each module - matching actual TypeScript types
const sampleDnsData: DnsModuleData = {
  score: 85,
  records: {
    a: ["192.168.1.1", "192.168.1.2"],
    aaaa: ["2001:db8::1"],
    mx: [{ exchange: "mail.example.com", priority: 10 }],
    ns: ["ns1.example.com", "ns2.example.com"],
    txt: ["v=spf1 include:_spf.google.com ~all", "google-site-verification=abc123"],
    dnssec: { enabled: true, validated: true },
  },
  subdomains: [
    { subdomain: "www", ips: ["192.168.1.1"], sources: ["ct_logs"] },
    { subdomain: "api", ips: ["192.168.1.2"], sources: ["dns"] },
  ],
  issues: [{ id: "dns-1", severity: "low", message: "Consider adding CAA records", module: "dns" }],
};

const sampleEmailData: EmailModuleData = {
  score: 72,
  grade: "B",
  spf: {
    record: "v=spf1 include:_spf.google.com ~all",
    valid: true,
    all_qualifier: "~all",
    dns_lookups: 4,
    mechanisms: ["include:_spf.google.com"],
    issues: [],
  },
  dkim: {
    selectors_found: [
      { selector: "google", key_size: 2048, algorithm: "rsa-sha256", valid: true },
      { selector: "default", key_size: 2048, algorithm: "rsa-sha256", valid: true },
    ],
    issues: [],
  },
  dmarc: {
    record: "v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com",
    policy: "quarantine",
    subdomain_policy: "none",
    pct: 100,
    rua: ["mailto:dmarc@example.com"],
    issues: ["Consider upgrading to p=reject policy"],
  },
  mx_records: ["aspmx.l.google.com"],
  issues: [],
};

const sampleTlsData: TlsModuleData = {
  overall_score: 90,
  hosts_scanned: 2,
  hosts_with_tls: 2,
  results: [
    {
      host: "example.com",
      port: 443,
      protocol_version: "TLSv1.3",
      cipher_suite: "TLS_AES_256_GCM_SHA384",
      certificate: {
        subject: "example.com",
        issuer: { organization: "Let's Encrypt", common_name: "R3" },
        not_before: "2024-01-01T00:00:00Z",
        not_after: "2025-01-01T00:00:00Z",
        days_until_expiry: 180,
        key_bits: 2048,
        key_algorithm: "RSA",
        signature_algorithm: "SHA256withRSA",
        san: ["example.com", "www.example.com"],
        is_self_signed: false,
      },
      chain_quality: { is_complete: true, is_ordered: true, has_trusted_root: true },
      issues: [],
    },
  ],
  inventory_summary: {
    total_certificates: 2,
    expired_certificates: 0,
    expiring_soon: 0,
    wildcard_certificates: 1,
  },
  issues: [],
};

const sampleHeadersData: HeadersModuleData = {
  overall_score: 65,
  grade: "C",
  hosts_scanned: 1,
  hosts_reachable: 1,
  results: [
    {
      url: "https://example.com",
      status_code: 200,
      headers: {
        strict_transport_security: { max_age: 31536000, include_subdomains: true, preload: false },
        x_frame_options: "DENY",
        x_content_type_options: "nosniff",
        referrer_policy: "strict-origin-when-cross-origin",
      },
      issues: ["Missing Content-Security-Policy header", "Missing Permissions-Policy header"],
    },
  ],
  issues: [
    { id: "hdr-1", severity: "high", message: "Missing Content-Security-Policy header", module: "headers" },
  ],
};

const sampleWhoisData: WhoisModuleData = {
  domain_info: {
    tld: "com",
    registrar: {
      name: "GoDaddy",
      iana_id: 146,
      url: "https://www.godaddy.com",
      abuse_email: "abuse@godaddy.com",
    },
    creation_date: "2010-01-15T00:00:00Z",
    expiry_date: "2025-06-15T00:00:00Z",
    updated_date: "2024-01-15T00:00:00Z",
    name_servers: ["ns1.example.com", "ns2.example.com"],
    status: ["clientTransferProhibited", "clientDeleteProhibited"],
    dnssec: true,
    privacy_protected: true,
  },
  issues: [],
};

const sampleTechData: TechModuleData = {
  score: 70,
  technologies: [
    { name: "React", version: "18.2.0", category: "framework", confidence: "high" },
    { name: "Nginx", version: "1.24.0", category: "server", confidence: "high" },
    { name: "Node.js", version: "20.10.0", category: "language", confidence: "medium" },
    { name: "WordPress", version: "6.4.2", category: "cms", confidence: "high" },
  ],
  headers: {
    server: "nginx/1.24.0",
    x_powered_by: "PHP/8.2",
  },
  issues: [
    { id: "tech-1", severity: "medium", message: "Server version disclosed in headers", module: "tech" },
  ],
};

const sampleCloudData: CloudModuleData = {
  score: 40,
  assets: [
    { name: "example-bucket", provider: "aws", access_level: "public_read", files_exposed: 150, url: "https://example-bucket.s3.amazonaws.com" },
    { name: "secure-storage", provider: "gcp", access_level: "private", files_exposed: 0 },
    { name: "public-assets", provider: "azure", access_level: "public_write", files_exposed: 50 },
  ],
  issues: [
    { id: "cloud-1", severity: "critical", message: "Publicly writable storage bucket found", module: "cloud" },
    { id: "cloud-2", severity: "high", message: "Publicly readable storage with 150 files exposed", module: "cloud" },
  ],
};

const sampleAsnData: AsnModuleData = {
  score: 85,
  asns: [
    { asn: 15169, name: "GOOGLE", description: "Google LLC", country_code: "US", rir: "ARIN", ipv4_prefix_count: 234, ipv6_prefix_count: 45 },
    { asn: 13335, name: "CLOUDFLARENET", description: "Cloudflare, Inc.", country_code: "US", rir: "ARIN", ipv4_prefix_count: 156, ipv6_prefix_count: 32 },
  ],
  prefixes: [
    { prefix: "8.8.8.0/24", ip_version: 4, asn: 15169, name: "Google DNS", country_code: "US", num_ips: 256 },
    { prefix: "8.8.4.0/24", ip_version: 4, asn: 15169, name: "Google DNS Secondary", country_code: "US", num_ips: 256 },
    { prefix: "142.250.0.0/16", ip_version: 4, asn: 15169, name: "Google Services", country_code: "US", num_ips: 65536 },
    { prefix: "104.16.0.0/12", ip_version: 4, asn: 13335, name: "Cloudflare CDN", country_code: "US", num_ips: 1048576 },
    { prefix: "2606:4700::/32", ip_version: 6, asn: 13335, name: "Cloudflare IPv6", country_code: "US", num_ips: 79228162514264337593543950336 },
  ],
  resolved_ips: ["142.250.80.46", "142.250.80.78"],
  total_ips: 1114624,
  is_shared_hosting: false,
  hosting_provider: "Google Cloud Platform",
  issues: [],
};

const sampleReposData: ReposModuleData = {
  score: 25,
  repositories_scanned: 5,
  repositories: [
    { name: "example-app", full_name: "org/example-app", url: "https://github.com/org/example-app", owner: "org", is_fork: false, stars: 100, last_updated: "2024-01-15", secrets_found: 3 },
  ],
  secrets: [
    { secret_type: "aws_access_key", severity: "critical", file_path: "config/settings.py", line_number: 42, snippet: "AWS_ACCESS_KEY_ID = 'AKIA...", repository: "example-app", repository_url: "https://github.com/org/example-app", owner: "org", match_pattern: "AKIA[A-Z0-9]{16}", discovered_at: "2024-01-15T10:00:00Z" },
    { secret_type: "api_key", severity: "high", file_path: ".env.example", line_number: 5, snippet: "API_KEY=sk_live_...", repository: "example-app", repository_url: "https://github.com/org/example-app", owner: "org", match_pattern: "sk_live_[a-zA-Z0-9]+", discovered_at: "2024-01-15T10:00:00Z" },
  ],
  issues: [
    { id: "repo-1", severity: "critical", message: "AWS credentials found in public repository", module: "repos" },
  ],
};

const sampleCrawlHintsData: CrawlHintsModuleData = {
  score: 75,
  robots: {
    found: true,
    rules: [
      { user_agent: "*", rule_type: "disallow", path: "/admin", is_sensitive: true, sensitivity_reason: "Admin panel path", severity: "medium" },
      { user_agent: "*", rule_type: "disallow", path: "/api/internal", is_sensitive: true, sensitivity_reason: "Internal API", severity: "high" },
    ],
    sitemaps: ["https://example.com/sitemap.xml"],
    sensitive_paths: [
      { path: "/admin", category: "admin", severity: "medium", reason: "Admin panel path disclosed" },
      { path: "/api/internal", category: "api", severity: "high", reason: "Internal API endpoint disclosed" },
    ],
  },
  security_txt: {
    found: true,
    contact: ["security@example.com"],
    policy: "https://example.com/security-policy",
    expires: "2025-12-31T23:59:59Z",
    preferred_languages: ["en"],
    is_valid: true,
    validation_issues: [],
  },
  sitemaps: [{ found: true, url: "https://example.com/sitemap.xml", entries: [], nested_sitemaps: [], total_urls: 150 }],
  well_known: {
    discovered: [
      { path: "/.well-known/security.txt", status_code: 200, content_type: "text/plain", description: "Security contact information", has_content: true },
    ],
  },
  issues: [],
};

const sampleJsAnalysisData: JsAnalysisModuleData = {
  score: 55,
  js_files_analyzed: [
    { url: "/js/app.js", size_bytes: 250000, is_minified: true, is_bundled: true, has_source_map: true, source_map_url: "/js/app.js.map", framework_hints: ["react"] },
  ],
  endpoints: [
    { path: "/api/v1/users", category: "api", http_methods: ["GET", "POST"], parameters: ["id", "name"], source_file: "/js/app.js", confidence: "high" },
    { path: "/api/v1/admin/settings", category: "admin", http_methods: ["POST"], parameters: [], source_file: "/js/app.js", confidence: "medium" },
  ],
  secrets: [
    { secret_type: "generic_api_key", severity: "high", value_preview: "sk_live_****", source_file: "/js/app.js", line_number: 145, context: "const API_KEY = ...", confidence: "high" },
  ],
  integrations: [
    { name: "Google Analytics", category: "analytics", identifiers: ["UA-12345"], endpoints_used: ["https://www.google-analytics.com"], confidence: "high" },
    { name: "Stripe", category: "payment", identifiers: ["pk_live"], endpoints_used: ["https://api.stripe.com"], confidence: "high" },
  ],
  source_maps: [
    { url: "/js/app.js.map", original_files: ["src/index.tsx", "src/App.tsx"], exposes_source: true, sensitive_paths: [] },
  ],
  frameworks_detected: ["React", "Redux"],
  frameworks_with_versions: [{ name: "React", version: "18.2.0" }],
  issues: [
    { id: "js-1", severity: "high", message: "API key exposed in JavaScript bundle", module: "js_analysis" },
    { id: "js-2", severity: "medium", message: "Source maps publicly accessible", module: "js_analysis" },
  ],
};

const sampleFaviconData: FaviconModuleData = {
  score: 80,
  favicon_url: "https://example.com/favicon.ico",
  favicon_found: true,
  hash: 116323821,
  hash_hex: "6ee28ed",
  file_size: 1150,
  content_type: "image/x-icon",
  known_match: {
    name: "Apache Tomcat",
    category: "server",
    risk_level: "medium",
    description: "Default Apache Tomcat favicon detected",
    cves: ["CVE-2020-1938", "CVE-2019-0232"],
  },
  shodan_query: "http.favicon.hash:116323821",
  issues: [
    { id: "fav-1", severity: "medium", message: "Default application favicon exposes server technology", module: "favicon" },
  ],
};

const sampleSubdomainsData: SubdomainsModuleData = {
  score: 70,
  total_found: 25,
  sources_used: ["ct_logs", "dns", "bruteforce"],
  subdomains: [
    { subdomain: "www.example.com", sources: ["ct_logs", "dns"], ips: ["192.168.1.1"] },
    { subdomain: "api.example.com", sources: ["dns"], ips: ["192.168.1.2"] },
    { subdomain: "staging.example.com", sources: ["bruteforce"], ips: ["192.168.1.3"] },
    { subdomain: "old.example.com", sources: ["ct_logs"], ips: [] },
    { subdomain: "dev.example.com", sources: ["bruteforce"], ips: ["192.168.1.5"] },
  ],
  takeover_findings: [
    { subdomain: "dev.example.com", cname: "old-service.herokuapp.com", service: "Heroku", vulnerability: "Dangling CNAME", confidence: "high" },
  ],
  issues: [
    { id: "sub-1", severity: "high", message: "Potential subdomain takeover vulnerability on dev.example.com", module: "subdomains" },
  ],
};

const samplePortscanData: PortscanModuleData = {
  score: 60,
  target: "example.com",
  ip: "192.168.1.1",
  tcp_ports: [
    { port: 22, protocol: "tcp", state: "open", service_hint: "ssh", service: { name: "ssh", product: "OpenSSH", version: "8.9", banner: "SSH-2.0-OpenSSH_8.9", confidence: 95, method: "banner" }, response_time_ms: 15 },
    { port: 80, protocol: "tcp", state: "open", service_hint: "http", service: { name: "http", product: "nginx", version: "1.24.0", confidence: 90, method: "http_header" }, response_time_ms: 12 },
    { port: 443, protocol: "tcp", state: "open", service_hint: "https", service: { name: "https", product: "nginx", version: "1.24.0", confidence: 90, method: "tls_cert" }, response_time_ms: 25 },
    { port: 3306, protocol: "tcp", state: "open", service_hint: "mysql", service: { name: "mysql", product: "MySQL", version: "8.0", confidence: 85, method: "banner" }, response_time_ms: 20 },
    { port: 23, protocol: "tcp", state: "open", service_hint: "telnet", service: { name: "telnet", confidence: 70, method: "port_lookup" }, response_time_ms: 10 },
    { port: 6379, protocol: "tcp", state: "open", service_hint: "redis", service: { name: "redis", product: "Redis", version: "7.0", confidence: 85, method: "banner" }, response_time_ms: 8 },
  ],
  stats: {
    total_ports_scanned: 1000,
    tcp_open: 6,
    tcp_closed: 990,
    tcp_filtered: 4,
    udp_open: 0,
    udp_filtered: 0,
    avg_response_time_ms: 15,
  },
  issues: [
    { id: "port-1", severity: "critical", message: "Telnet (port 23) is open - unencrypted protocol", module: "portscan" },
    { id: "port-2", severity: "high", message: "MySQL (port 3306) exposed to the internet", module: "portscan" },
    { id: "port-3", severity: "high", message: "Redis (port 6379) exposed without authentication", module: "portscan" },
  ],
};

export default function ModuleDemo() {
  return (
    <div className="space-y-8 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Module Visualization Demo</h1>
        <p className="text-muted-foreground mt-2">
          All 14 ASM Toolkit module visualizations with sample data
        </p>
      </div>

      {/* Passive Modules */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 pb-2 border-b">Passive Modules</h2>
        <div className="space-y-6">
          <DnsModuleView data={sampleDnsData} />
          <EmailModuleView data={sampleEmailData} />
          <WhoisModuleView data={sampleWhoisData} />
          <TlsModuleView data={sampleTlsData} />
          <HeadersModuleView data={sampleHeadersData} />
          <TechModuleView data={sampleTechData} />
          <CloudModuleView data={sampleCloudData} />
          <AsnModuleView data={sampleAsnData} />
          <ReposModuleView data={sampleReposData} />
          <CrawlHintsModuleView data={sampleCrawlHintsData} />
          <JsAnalysisModuleView data={sampleJsAnalysisData} />
          <FaviconModuleView data={sampleFaviconData} />
        </div>
      </section>

      {/* Active Modules */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 pb-2 border-b">Active Modules (Require Domain Verification)</h2>
        <div className="space-y-6">
          <SubdomainsModuleView data={sampleSubdomainsData} />
          <PortscanModuleView data={samplePortscanData} />
        </div>
      </section>
    </div>
  );
}
